import * as React from 'react';
import {
    DataGrid,
    GridColDef,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridRenderCellParams,
    GridValueGetterParams,
} from '@mui/x-data-grid';
import { Avatar, Box, Popover, Typography } from '@mui/material';
import { IState } from '../../../../../redux/reducers/rootReducers';
import { connect } from 'react-redux';
import { ITicket } from '../../../../../redux/actions/TicketsManagement/TicketsManagement';
import { ContactPopover } from '../../../../TicketManagementSystem/Modules/TicketCard';
import { ConvertZ } from '../../../../helpers/helperFunctions';
import timeSpanToReadableFormat from '../../../../../util/timeSpanToReadbleFormat';
import TicketActionsNew from '../../TicketActionNew';

function CustomToolbar() {
    return (
        <GridToolbarContainer>
            <GridToolbarColumnsButton />
        </GridToolbarContainer>
    );
}

interface ListViewTableProps {
    tickets: ITicket[];
    dispatch: React.Dispatch<any>;
}

const ListViewTable: React.FC<ListViewTableProps> = ({ tickets, dispatch }) => {

    const bindData = (params: GridRenderCellParams|any) => {
        const value = params.field.split('.').reduce((acc: any, key: string) => acc?.[key], params.row);
        
        if (params.colDef.isDateField) {
            if (!value) return '—';
            const date = ConvertZ(value);
            return date ?? "-";
        }
        if (params.colDef.isPriorityField) {
            return <Typography component={"span"} style={{color : params.row.ticketLogs?.priority?.color || "inherit"}}>{value ?? "-"}</Typography>;
        }
        if (params.colDef.isSLAField) {
            const formattedSLA:string = timeSpanToReadableFormat(value, "ss");
            const color = formattedSLA.includes("-") ? "red" : "green";
            return <Typography component={"span"} style={{color}}> {formattedSLA || "NO SLA"}</Typography>;
        }
        
        return value ?? '—';
    };

    const getValue = (params:GridValueGetterParams|any) => {
       const value = params.field.split('.').reduce((acc: any, key: string) => acc?.[key], params.row);
        if (params.colDef.isDateField) {
            if (!value) return '—';
            return new Date(value).getTime();
        }
        if (params.colDef.isSLAField) {
            return params.row.ticketLogs.slaRemainingInTicks||0;
        }
       
       return value ?? '—';
    }

    const getContactBox = (params: GridRenderCellParams) => {
        return (<><GetContactBox {...params}/></>);
    }

    const bindActions = (params: GridRenderCellParams) => {
        const { row } = params;
        return (
        <Box className="rightAlignedActions">
            <TicketActionsNew ticketData={row}/>
        </Box>)
    }

    const bindIdColumn = (params: GridRenderCellParams) => {
        return (
            <Typography 
                onClick={()=>{
                    dispatch({
                          type: "TICKET_VIEW_INFO",
                          payload: {ticketId:params.row.id,type:"quick-update"},
                        });
                    }} 
                component={"span"} 
                className='pointer'
            > 
                {params?.row?.id}
            </Typography>
        );
    }
    
    
    const columns: GridColDef[] = [
        {
            field: 'id',
            headerName: 'ID',
            renderCell: bindIdColumn,
            valueGetter: getValue,
        },
        {
            field: 'ticketLogs.contacts.name',
            headerName: 'Contact',
            renderCell: getContactBox,
            valueGetter: getValue,
            sortable: true,
            width: 90,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            sortable: false,
            width : 180,
            filterable: false,
            disableColumnMenu: true,
            renderCell: bindActions
        }
    ];

    const additionalColumns: GridColDef[] = Object.entries(schema).map(([key, value]) => ({
        field: key,
        headerName: value.headerName,
        sortable: value.sortable,
        isSLAField: value.isSLAField || false,
        isDateField: value.isDateField || false,
        isPriorityField : value.isPriorityField || false,
        width: value?.width || 150,
        valueGetter: getValue,
        renderCell:bindData
    }));
    return (
        <Box sx={{height: 'auto',  zIndex:1, }}>
           <DataGrid
                className="ticketListViewTable"
                rows={tickets}
                columns={columns.concat(additionalColumns)}
                checkboxSelection={false}
                disableRowSelectionOnClick
                components={{
                    Toolbar: CustomToolbar,
                }}
                autoHeight
                sx={(theme) => ({
                                      backgroundColor: 'inherit',
                    '& .MuiDataGrid': {
                    height: '54vh',
                    zIndex: 1,
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                    position: 'fixed',
                    zIndex: 1,
                    backgroundColor:  theme.palette.mode === "dark"
                    ? "#4a4a4e"
                    : "#F0F3FE", // auto adapt
                    width: '93vw',
                    mb: 10,
                    },
                    '& .MuiDataGrid-columnHeaders': {
                    position: 'fixed',
                    width: '93vw',
                    zIndex: 1,
                    mt: 4,
                    backgroundColor:  theme.palette.mode === "dark"
                    ? "#4a4a4e"
                    : "#F0F3FE", // auto adapt
                    },
                    '& .MuiDataGrid-virtualScroller': {
                    mt: 10,
                    overflowY: 'auto !important',
                    },
                    '& .MuiDataGrid-footerContainer': {
                    position: 'sticky',
                    bottom: -5,
                    backgroundColor: theme.palette.mode === "dark"
            ? "#4a4a4e"
            : "#F0F3FE", // auto adapt
                },
            })}
            />

        </Box>
    );
}

const GetContactBox = (params: GridRenderCellParams) => {
    const contact = params?.row?.ticketLogs?.contacts || {};
    const secondaryContact = params?.row?.ticketLogs?.secondaryContacts || {};

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'contact-popover' : undefined;


    return (
        <Box className="imgBox">
            <Box>
                <button
                    type="button"
                    onClick={handleClick}
                    style={{ background: "none", border: "none", padding: 0, margin: 0, cursor: "pointer" }}
                >
                    <Avatar
                        className="sml-avatar"
                        sx={{ height: 25, width: 25 }}
                        alt={contact?.name || "PP"}
                        src={`${contact?.profilePicturePath || ""}`}
                    />
                </button>

                <Popover
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                >
                    <Box sx={{ p: 1 }}>
                        <ContactPopover contactInfo={contact} secContactInfo={secondaryContact} />
                    </Box>
                </Popover>
            </Box>
        </Box>
    );
}


interface ColumnSchema extends Omit<GridColDef, 'field'> {
  isDateField?: boolean;
  isSLAField?: boolean;
  width?: number;
  isPriorityField?: boolean;
}

const schema: Record<string, ColumnSchema>  = { 
    "ticketLogs.title" : { "headerName": "Title", "width" : 300 , "sortable": true },
    "ticketLogs.ticketStatus.displayName" : { "headerName": "Status", "sortable": true },
    "ticketLogs.ticketType.displayName" : { "headerName": "Ticket Type", "sortable": true },
    "ticketLogs.group.name" : { "headerName": "Group", "sortable": true },
    "ticketLogs.agent.fullName" : { "headerName": "Agent", "sortable": true },
    "ticketLogs.dueDateTime" : { "headerName": "Due Date", "width" : 200, "isDateField" : true, "sortable": true },
    "ticketLogs.priority.name" : { "headerName": "Priority", "sortable": true, "isPriorityField": true },
    "ticketLogs.slaRemaining" : { "headerName": "SLA", "isSLAField" : true }
}

const mapStateToProps = (state: IState) => {
    return ({
        tickets: state?.AllTicketsReducer?.otherTickets,
    })
};

export default connect(mapStateToProps)(ListViewTable);